import type {
  TimelineProviderRequestParams,
  CameraExid,
  DetectionsPresenceByLabel,
} from "@evercam/shared/types"
import { TimelineDataProvider } from "./timelineDataProvider"
import { DetectionLabel } from "@evercam/shared/types"
import { AiApi } from "@evercam/shared/api/aiApi"
import { TimelineRangeEvent } from "@evercam/ui"

export class TimelineObjectDetectionIntervalsProvider extends TimelineDataProvider {
  private static presenceIntervalsPromise: Promise<DetectionsPresenceByLabel> | null =
    null
  private static onIntervalsChange: (
    intervalsByLabel: DetectionsPresenceByLabel
  ) => void
  readonly cameraExid: CameraExid
  readonly labelFilterFn: (label: DetectionLabel) => boolean

  constructor(params: {
    timezone: string
    cameraExid: CameraExid
    labelFilterFn: (label: DetectionLabel) => boolean
  }) {
    super(params.timezone)
    this.cameraExid = params.cameraExid
    this.labelFilterFn = params.labelFilterFn || ((_) => true)
  }

  static registerIntervalsChangeCallback(
    callback: (intervalsByLabel: DetectionsPresenceByLabel) => void
  ) {
    TimelineObjectDetectionIntervalsProvider.onIntervalsChange = callback
  }

  async fetchEvents(
    params: TimelineProviderRequestParams
  ): Promise<Array<TimelineRangeEvent>> {
    if (!TimelineObjectDetectionIntervalsProvider.presenceIntervalsPromise) {
      TimelineObjectDetectionIntervalsProvider.presenceIntervalsPromise =
        this.doFetchIntervals(params).finally(() => {
          TimelineObjectDetectionIntervalsProvider.presenceIntervalsPromise =
            null
        })
    }

    return TimelineObjectDetectionIntervalsProvider.presenceIntervalsPromise.then(
      (intervalsByLabel) => {
        if (TimelineObjectDetectionIntervalsProvider.onIntervalsChange) {
          TimelineObjectDetectionIntervalsProvider.onIntervalsChange(
            intervalsByLabel
          )
        }

        const entry = Object.entries(intervalsByLabel).find(
          ([label, _intervals]) => this.labelFilterFn(label as DetectionLabel)
        )

        if (!entry) {
          return []
        }

        const intervals = entry[1]

        return this.processResponseIntervals(intervals, params.precision!)
      }
    )
  }

  async fetchCounts(
    params: TimelineProviderRequestParams
  ): Promise<Array<TimelineRangeEvent>> {
    return this.fetchEvents(params)
  }

  private async doFetchIntervals(
    params: TimelineProviderRequestParams
  ): Promise<DetectionsPresenceByLabel> {
    return await AiApi.detections.getDetectionsPresenceDateIntervals({
      cameraExid: this.cameraExid,
      fromDate: new Date(params.fromDate).toISOString(),
      toDate: new Date(params.toDate).toISOString(),
      precision: params.precision,
    })
  }
}
